import { useEffect, useState, useMemo } from "react";
import { useSession } from "@clerk/react";
import { getMyMeetsUpComing as getMyMeetsUpComingApi } from "@/api/meets";
import ActiveMeetingCard from "@/components/v2/meeting-card";
import { CalendarCheck } from "lucide-react";
import { Skeleton } from "@heroui/react";
import { formatDateRelative } from "@/utils/dates";
import PulledDownWrapper from "../wrappers/pulled-down-wrapper";
import NoMeetsComp from "./no-meets-comp";
import MeetingCardSkeleton from "../v2/meeting-card-skeleton";
import { matchesMeetingSearch } from "@/utils/meeting-search";

const UpComingMeetsComp = ({ setNumMeetings, searchQuery }) => {
  const { session } = useSession();
  const [records, setRecord] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    getMyMeetsUpComing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRecords = useMemo(
    () => records.filter((rec) => matchesMeetingSearch(rec, searchQuery)),
    [records, searchQuery],
  );

  useEffect(() => {
    setNumMeetings(filteredRecords.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRecords]);

  async function getMyMeetsUpComing() {
    setIsLoading(true);
    setFetchError(false);
    const token = await session.getToken();
    getMyMeetsUpComingApi(token)
      .then((_resp) => {
        if (_resp.data.Result === "OK") {
          setRecord(_resp.data.Records);
        } else {
          throw new Error("Failed to load upcoming meetings");
        }
      })
      .catch((_resp) => {
        console.error(_resp);
        setFetchError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  const groups = useMemo(() => {
    const map = new Map();
    for (const rec of filteredRecords) {
      const pretty = formatDateRelative(rec.meeting_date);
      if (!map.has(pretty)) {
        map.set(pretty, { key: rec.meeting_date, pretty, items: [] });
      }
      map.get(pretty).items.push(rec);
    }
    return Array.from(map.values());
  }, [filteredRecords]);

  return (
    <PulledDownWrapper onRefresh={getMyMeetsUpComing}>
      <div className="max-w-[900px] gap-2 grid grid-cols-12">
        {isLoading ? (
          <>
            {[0, 1, 2].map((i) => (
              <div key={i} className="col-span-12 flex flex-col mb-2">
                <MeetingCardSkeleton />
              </div>
            ))}
          </>
        ) : fetchError ? (
          <div className="col-span-12 flex flex-col items-center gap-3 py-10 text-center text-glass-foreground">
            <p className="font-semibold">Something went wrong!</p>
            <p className="text-sm text-glass-foreground-muted">We couldn't find your meetings.</p>
            <button className="text-sm underline" onClick={getMyMeetsUpComing}>
              Try again
            </button>
          </div>
        ) : groups.length > 0 ? (
          groups.map((group, gi) => (
            <div key={group.key} className="contents">
              <div className="col-span-12">
                <div className="text-start uppercase text-[10px] font-bold pb-2 tracking-[1.02px] text-glass-foreground">{group.pretty}</div>
              </div>
              {group.items.map((rec, ri) => {
                const isLastGroup = gi === groups.length - 1;
                const isLastCard = ri === group.items.length - 1;
                const mb = isLastGroup && isLastCard ? "mb-40" : "mb-2";
                return (
                  <div key={rec._id} className={`col-span-12 flex flex-col ${mb}`}>
                    <ActiveMeetingCard _record={rec} _onResp={null} />
                  </div>
                );
              })}
            </div>
          ))
        ) : (
          <NoMeetsComp title={searchQuery ? "No matching Loopps" : "No upcoming Loopps!"} />
        )}
      </div>
    </PulledDownWrapper>
  );
};

export default UpComingMeetsComp;